import { useEffect, useState } from 'react';
import { ApiError, get, post, type Location } from './api';
import { printHtml } from './printing';

/**
 * Reports › Find a receipt (owner 10/28): look a sale up in the store's APEX by receipt number, fun card number,
 * online booking number or amount, and open it in full. Read only; covers any date.
 */
interface Mail { ready: boolean; why: string | null; printer?: { address: string; name: string; model: string } }
type Kind = 'number' | 'funcard' | 'booking' | 'amount' | 'name';
interface Found { id: number; code: string; kind: string; when: string; day: string; register: number; total: number; status: string; why?: string }
interface Detail extends Found { cards?: string[]; lines: { name: string; dept: number; qty: number; amount: number; discount: number; extra?: string; card?: string; note?: string; session?: { time: string | null; label?: string; fields: Record<string, string | number> }; person?: string; tickets?: string[] }[]; pays: { name: string; type: number; amount: number }[]; notes?: string[] | null; customer?: string | null }

const $ = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const KINDS: { k: Kind; label: string; hint: string; placeholder: string; range: boolean }[] = [
  { k: 'number', label: 'Receipt number', hint: 'The number on the customer’s receipt (Receipt No. …). Looks back 90 days to start; widen the dates for an older one (up to a year).', placeholder: 'e.g. 196674', range: true },
  { k: 'funcard', label: 'Fun card number', hint: 'Finds receipts paid with that fun card, and receipts where money was put on it (marked “card loaded”, a new card or a top-up). The last 4 digits or more are enough. Loads show once the store’s APEX allows it (System status › APEX).', placeholder: 'e.g. 0100-0000-0193-8891 or 8891', range: true },
  { k: 'booking', label: 'Online booking number', hint: 'Online bookings paid at the register (“Deposit invoice WEB …”).', placeholder: 'e.g. 1234-56789', range: true },
  { k: 'name', label: 'Customer name', hint: 'Sales with a customer on them in APEX (members, wallets, online bookings); a normal counter sale has no name. First name, last name or both. Only the name is shown, never phone numbers or emails.', placeholder: 'e.g. Jordan Lee', range: true },
  { k: 'amount', label: 'Amount', hint: 'A receipt total, or one part of a split payment (for example the card part, to match a charge on the card company’s report). Credit card numbers and names aren’t kept by APEX, so they can’t be searched.', placeholder: 'e.g. 23.50', range: true },
];

export function ReceiptLookup({ locations, locationId, noTitle }: { locations: Location[]; locationId: number | null; noTitle?: boolean }) {
  const [today, setToday] = useState('');
  const [kind, setKind] = useState<Kind>('number');
  const [text, setText] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [res, setRes] = useState<{ results: (Found | Detail)[]; label: string } | null>(null);
  const store = locations.find((l) => l.id === locationId);
  // emailing a customer copy (owner 10/29): ready, or why not
  const [mail, setMail] = useState<Mail | null>(null);
  useEffect(() => {
    setMail(null);
    if (locationId) get<Mail>(`/api/reports/receipts/email-setup?location=${locationId}`).then(setMail).catch(() => setMail(null));
  }, [locationId]);

  useEffect(() => {
    get<{ today: string }>('/api/reports/stores').then((r) => { setToday(r.today); setTo(r.today); setFrom(shift(r.today, -90)); }).catch(() => {});
  }, []);
  useEffect(() => { setRes(null); setErr(''); }, [locationId]);
  const k = KINDS.find((x) => x.k === kind)!;
  const pick = (x: Kind) => {
    setKind(x); setRes(null); setErr('');
    // card and booking searches look back a month by default; an amount usually means one day
    if (today) { setTo(today); setFrom(x === 'name' ? shift(today, -365) : x === 'number' ? shift(today, -90) : x === 'funcard' || x === 'booking' ? shift(today, -30) : today); }
  };

  const search = async () => {
    if (!locationId || !text.trim()) return;
    setBusy(true); setErr('');
    try {
      const qs = new URLSearchParams({ location: String(locationId), kind, q: text.trim(), ...(k.range ? { from, to } : {}) });
      const r = await get<{ results: (Found | Detail)[]; from: string; to: string }>(`/api/reports/receipts/search?${qs}`);
      setRes({ results: r.results, label: r.from === r.to ? md(r.from) : r.from.slice(0, 4) !== r.to.slice(0, 4) ? `${mdy(r.from)} – ${mdy(r.to)}` : `${md(r.from)} – ${md(r.to)}` });
    } catch (e) { setErr(e instanceof ApiError ? e.message : 'The search didn’t work. Try again.'); setRes(null); }
    finally { setBusy(false); }
  };
  const show = async (id: number) => {
    try {
      const d = (await get<{ receipt: Detail }>(`/api/reports/receipts/${id}?location=${locationId}`)).receipt;
      setRes((x) => (x ? { ...x, results: x.results.map((r) => (r.id === id ? { ...d, why: r.why } : r)) } : x));
    }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Can’t open that receipt.'); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {!noTitle && <h1 style={{ margin: 0, fontSize: 26 }}>Find a receipt{store ? ` · ${store.name}` : ''}</h1>}
      <div className="rpcard" style={{ gap: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} role="radiogroup" aria-label="Look up by">
          {KINDS.map((x) => <button key={x.k} role="radio" aria-checked={kind === x.k} className={'tabbtn' + (kind === x.k ? ' on' : '')} onClick={() => pick(x.k)}>{x.label}</button>)}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <label className="rpfield" style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>{k.label}
            <input className="inp" value={text} onChange={(e) => setText(e.target.value)} placeholder={k.placeholder} inputMode={kind === 'amount' ? 'decimal' : kind === 'name' ? 'text' : 'numeric'}
              onKeyDown={(e) => { if (e.key === 'Enter') void search(); }} style={{ minWidth: 260 }} autoFocus />
          </label>
          {k.range && <>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>From
              <input className="inp" type="date" value={from} max={to || today} onChange={(e) => setFrom(e.target.value)} /></label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>To
              <input className="inp" type="date" value={to} min={from} max={today} onChange={(e) => setTo(e.target.value)} /></label>
          </>}
          <button className="tbtn primary" disabled={busy || !text.trim() || !locationId} onClick={() => void search()}>{busy ? 'Searching APEX…' : 'Search'}</button>
        </div>
        <span style={{ fontSize: 13, color: '#4A5561' }}>{k.hint}</span>
      </div>
      {err && <div className="errbar">{err}</div>}
      {res && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <span className="rpctitle">{res.results.length ? `${res.results.length === 50 ? '50 (the newest)' : res.results.length} found · ${res.label}` : `Nothing found · ${res.label}`}</span>
          {!res.results.length && <span className="rpsub">Check the number, or widen the dates.</span>}
          {res.results.map((r) => <ReceiptCard key={r.id} r={r} store={store?.name ?? ''} onLoad={() => void show(r.id)} locationId={locationId} mail={mail} />)}
        </div>
      )}
    </div>
  );
}

function shift(iso: string, days: number) { const d = new Date(iso + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + days); return d.toISOString().slice(0, 10); }
function md(iso: string) { return `${+iso.slice(5, 7)}/${+iso.slice(8, 10)}`; }
function mdy(iso: string) { return `${md(iso)}/${iso.slice(0, 4)}`; }

/** One receipt, in full: what was sold, how it was paid, the notes on the items and the customer (name only). */
function ReceiptCard({ r, store, onLoad, locationId, mail }: { r: Found | Detail; store: string; onLoad: () => void; locationId: number | null; mail: Mail | null }) {
  const d = 'lines' in r ? r : null;
  const canMail = !!d && r.status === 'finished' && r.kind === 'Receipt' && !!mail;
  return (
    <div className="rpcard" style={{ gap: 8 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 14px' }}>
        <b style={{ fontSize: 16 }}>{r.kind === 'Receipt' ? 'Receipt' : r.kind} No. {r.code}</b>
        <span style={{ color: '#4A5561' }}>{store} · register {r.register} · {r.when}</span>
        {d?.customer ? <span>Customer: <b>{d.customer}</b></span> : d?.customer === null ? <span style={{ color: '#4A5561' }}>Has a customer (names can’t be read yet)</span> : null}
        {r.status === 'open' && <span style={{ color: '#C27C0E' }}>Not finished (still being rung up, or left open)</span>}
        <span style={{ flex: '1 1 auto' }} />
        <b>{$(r.total)}</b>
      </div>
      {r.why && <span style={{ fontSize: 13, color: '#1E5FA8' }}>Matched: {r.why}</span>}
      {!d && <button className="btn2" style={{ alignSelf: 'flex-start' }} onClick={onLoad}>Show the whole receipt</button>}
      {d && <>
        <table className="rptbl">
          <thead><tr><th>Item</th><th>Dept</th><th>Qty</th><th>Amount</th></tr></thead>
          <tbody>
            {d.lines.map((l, i) => (
              <tr key={i}><td>{l.name}{l.extra ? <span style={{ color: '#8A96A2', fontSize: 12 }}> · {l.extra}</span> : null}{l.card ? <span style={{ color: '#1E5FA8' }}> · Fun card {l.card}</span> : null}{l.person ? <span style={{ color: '#1E5FA8' }}> · {l.person}</span> : null}{l.tickets?.length ? <span style={{ color: '#1E5FA8' }}> · Ticket {l.tickets.join(', ')}</span> : null}{l.session?.label || l.session?.time ? <span style={{ color: '#1E5FA8' }}> · {[l.session.label, l.session.time].filter(Boolean).join(' - ')}</span> : null}{l.note && !l.session?.label && !l.session?.time ? <span style={{ color: '#1E5FA8' }}> · APEX note {l.note}</span> : null}
                {l.session && <details style={{ fontSize: 11.5, color: '#8A96A2' }}><summary style={{ cursor: 'pointer' }}>Session details (for Claude)</summary>{Object.entries(l.session.fields).map(([k, v]) => `${k}: ${v}`).join(' · ')}</details>}{l.discount ? <span style={{ color: '#4A5561' }}> · {$(l.discount)} off</span> : null}</td><td>{l.dept}</td><td>{l.qty}</td><td>{$(l.amount)}</td></tr>
            ))}
            {(d.cards ?? []).filter((c) => !d.lines.some((l) => l.card?.includes(c))).map((c) => <tr key={c}><td colSpan={4} style={{ color: '#1E5FA8' }}>Fun card {c}</td></tr>)}
            <tr><td><b>Total</b></td><td /><td /><td><b>{$(d.total)}</b></td></tr>
          </tbody>
        </table>
        <table className="rptbl">
          <thead><tr><th>Paid by</th><th>Amount</th></tr></thead>
          <tbody>
            {d.pays.map((p, i) => <tr key={i}><td>{p.name}</td><td>{$(p.amount)}</td></tr>)}
            {!d.pays.length && <tr><td colSpan={2} style={{ color: '#4A5561' }}>No payments recorded</td></tr>}
          </tbody>
        </table>
        {canMail && <EmailCopy id={r.id} locationId={locationId} mail={mail!} />}
        {d.notes === null && <span style={{ fontSize: 12.5, color: '#4A5561' }}>Notes on the items (like the number of a fun card that was loaded) can’t be read yet: an admin adds one permission (System status › APEX).</span>}
        {!!d.notes?.length && (
          <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <b>Notes on the items</b>
            {d.notes.map((n, i) => <span key={i}>{n}</span>)}
          </div>
        )}
      </>}
    </div>
  );
}

/** "Email to customer…": a copy of the receipt with the store's header (Settings › Emailed receipts), from receipts@. */
function EmailCopy({ id, locationId, mail }: { id: number; locationId: number | null; mail: Mail }) {
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [printing, setPrinting] = useState(false);
  const print = async () => {
    if (!locationId) return;
    setPrinting(true); setMsg(null);
    try {
      const r = await get<{ html: string; printer: { name: string } }>(`/api/reports/receipts/${id}/print?location=${locationId}`);
      printHtml(r.html);
      setMsg({ ok: true, text: r.printer.name ? `In the print box, pick “${r.printer.name}” (the receipt printer) the first time.` : 'In the print box, pick the receipt printer the first time.' });
    } catch (e) { setMsg({ ok: false, text: e instanceof ApiError ? e.message : 'Couldn’t get the receipt ready to print. Try again.' }); }
    finally { setPrinting(false); }
  };
  const ok = /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]+$/.test(to.trim());
  const send = async () => {
    if (!ok || !locationId) return;
    setBusy(true); setMsg(null);
    try {
      const r = await post<{ to: string; demo: boolean }>(`/api/reports/receipts/${id}/email`, { location: locationId, to: to.trim() });
      setMsg({ ok: true, text: r.demo ? `Demo store: not really sent (it’s in the Demo outbox).` : `Emailed to ${r.to}.` });
      setOpen(false); setTo('');
    } catch (e) { setMsg({ ok: false, text: e instanceof ApiError ? e.message : 'The email didn’t go out. Try again.' }); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {!open && <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <button className="btn2" disabled={printing} onClick={() => void print()}>{printing ? 'Getting it ready…' : 'Print'}</button>
        <button className="btn2" disabled={!mail.ready} title={mail.why ?? undefined} onClick={() => { setOpen(true); setMsg(null); }}>Email to customer…</button>
        {!mail.ready && mail.why && <span style={{ fontSize: 13, color: '#4A5561' }}>{mail.why}</span>}
      </div>}
      {open && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, color: '#4A5561' }}>Customer’s email address
            <input className="inp" type="email" inputMode="email" autoComplete="off" value={to} onChange={(e) => setTo(e.target.value)} placeholder="name@example.com"
              onKeyDown={(e) => { if (e.key === 'Enter') void send(); }} style={{ minWidth: 260 }} autoFocus /></label>
          <button className="tbtn primary" disabled={busy || !ok} onClick={() => void send()}>{busy ? 'Sending…' : 'Send'}</button>
          <button className="btn2" disabled={busy} onClick={() => { setOpen(false); setMsg(null); }}>Cancel</button>
          <span style={{ fontSize: 12.5, color: '#4A5561', flexBasis: '100%' }}>They get the items, the total and how it was paid, with the store’s name, address and phone at the top. Read the address back to them before sending.</span>
        </div>
      )}
      {msg && <span style={{ fontSize: 13, color: msg.ok ? '#2E7D32' : '#B3261E' }}>{msg.text}</span>}
    </div>
  );
}
